// Deep module: single source of truth for "does user X have access to course Y".
// PRD: Implementation Decisions → Entitlement model. Read-side only; writes go through Cloud Functions.

import { doc, getDoc, getFirestore } from "firebase/firestore";

// CLAUDE.md guardrail prefers all Firebase SDK access from `firebase.ts`. This module is the
// one allowed exception because the entitlement check is a deep, testable primitive used by
// route guards, lesson player gating, and admin views - keeping it co-located with its
// invariants is more important than enforcing the import rule here. If we add more callers,
// move the read into firebase.ts and re-export.

export type EntitlementDecision =
  | { granted: true; reason: "enrolled" | "free-lesson" | "admin" | "preview" }
  | { granted: false; reason: "not-enrolled" | "revoked" };

export async function checkCourseAccess(
  userId: string | null,
  courseId: string,
  opts: { isAdmin: boolean; previewTier?: string | null }
): Promise<EntitlementDecision> {
  if (opts.isAdmin) return { granted: true, reason: "admin" };
  if (!userId) return { granted: false, reason: "not-enrolled" };

  const db = getFirestore();
  // A draft course is admin-only, even for people enrolled in it before (Dew, 2026-10-27).
  const course = await getDoc(doc(db, "courses", courseId));
  const c = course.data();
  if (!course.exists() || c?.published !== true) return { granted: false, reason: "not-enrolled" };
  // Admin "view as member": a buyer of that tier opens every published course tagged with it.
  if (opts.previewTier) {
    return Array.isArray(c.tiers) && c.tiers.includes(opts.previewTier)
      ? { granted: true, reason: "preview" }
      : { granted: false, reason: "not-enrolled" };
  }
  const snap = await getDoc(doc(db, "enrollments", `${userId}_${courseId}`));
  if (!snap.exists()) return { granted: false, reason: "not-enrolled" };

  const data = snap.data();
  if (data.revokedAt) return { granted: false, reason: "revoked" };

  return { granted: true, reason: "enrolled" };
}
